import { Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { useAdminProductUpdates, useDeleteProductUpdate } from "@/api/admin/product-updates";
import { usePagination } from "@/api/pagination";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { useCache } from "@/hooks/use-cache";
import { useSort } from "@/hooks/use-sort";
import { EditProductUpdateModal } from "@/pages/admin/modals/product-update-modal";
import { formatShortDate } from "@/pages/admin/tables/format-date";
import { success } from "@/providers/ToastProvider";

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

type PopupStatus = "visible" | "hidden" | "expired";

const derivePopupStatus = (update: { showAsPopup: boolean; publishedAt: string | null }): PopupStatus => {
  if (!update.publishedAt || !update.showAsPopup) return "hidden";
  if (!update.publishedAt) return "hidden";

  const publishedMs = new Date(update.publishedAt).getTime();
  const isExpired = Date.now() - publishedMs > THIRTY_DAYS_MS;
  return isExpired ? "expired" : "visible";
};

const popupStatusConfig: Record<PopupStatus, { label: string; color: "gray" | "success" }> = {
  visible: { label: "Visible", color: "success" },
  hidden: { label: "Hidden", color: "gray" },
  expired: { label: "Expired", color: "gray" },
};

const PopupStatusBadge = ({ update }: { update: { showAsPopup: boolean; publishedAt: string | null } }) => {
  const status = derivePopupStatus(update);
  const config = popupStatusConfig[status];
  return (
    <BadgeWithDot size="sm" color={config.color}>
      {config.label}
    </BadgeWithDot>
  );
};

const PublishedBadge = ({ publishedAt }: { publishedAt: string | null }) => {
  if (!publishedAt) {
    return (
      <Badge size="sm" color="gray">
        Draft
      </Badge>
    );
  }
  return (
    <Badge size="sm" color="success">
      {formatShortDate(publishedAt)}
    </Badge>
  );
};

const stripFormatting = (copy: string) => copy.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/^- /gm, "");

const TruncatedCopy = ({ copy }: { copy: string }) => {
  const plain = stripFormatting(copy).replace(/\n+/g, " ").trim();
  return <p className="text-sm text-tertiary truncate max-w-md">{plain}</p>;
};

const DeleteProductUpdateModal = ({ productUpdate }: { productUpdate: { id: string; title: string } }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { mutateAsync } = useDeleteProductUpdate();

  const onSubmit = async () => {
    await mutateAsync({ id: productUpdate.id });
    success("Product update deleted");
    close();
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={close}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Delete" icon={Trash01} onClick={() => setOpen(true)} />
      <ModalOverlay isDismissable>
        <Modal className="dark-mode max-w-md">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    Delete product update
                  </Heading>
                </div>
                <p>
                  Are you sure you want to delete &ldquo;{productUpdate.title}&rdquo;? This action cannot be undone.
                </p>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close}>
                  Cancel
                </Button>
                <Button color="primary-destructive" size="lg" type="button" onClick={onSubmit}>
                  Delete
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

export const AdminProductUpdatesTable = () => {
  const pagination = usePagination();
  const sort = useSort<"createdAt" | "publishedAt" | "title">("createdAt");
  const { data } = useAdminProductUpdates(pagination.pagination, sort.sort);
  const total = useCache(data?.total);

  return (
    <TableCard.Root>
      <div className="overflow-auto">
        <Table aria-label="Product updates" sortDescriptor={sort.descriptor} onSortChange={sort.setFromDescriptor}>
          <Table.Header>
            <Table.Head id="title" label="Title" isRowHeader allowsSorting className="w-[180px]" />
            <Table.Head id="thumbnail" label="Thumbnail" className="w-[100px]" />
            <Table.Head id="copy" label="Copy" className="w-full" />
            <Table.Head id="publishedAt" label="Published" allowsSorting className="w-[120px]" />
            <Table.Head id="popupStatus" label="Pop-up Status" className="w-[130px]" />
            <Table.Head id="actions" label="" className="w-min-0" />
          </Table.Header>

          <Table.Body items={data?.productUpdates ?? []}>
            {(item) => (
              <Table.Row id={item.id}>
                <Table.Cell>
                  <p className="text-sm font-medium text-primary truncate max-w-[180px]">{item.title}</p>
                </Table.Cell>
                <Table.Cell>
                  {item.thumbnailUrl ? (
                    <img src={item.thumbnailUrl} alt="" className="h-10 w-16 rounded object-cover" />
                  ) : null}
                </Table.Cell>
                <Table.Cell>
                  <TruncatedCopy copy={item.copy} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <PublishedBadge publishedAt={item.publishedAt} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <PopupStatusBadge update={item} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <DeleteProductUpdateModal productUpdate={item} />
                    <EditProductUpdateModal productUpdate={item} />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
